import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
    BehaviorSubject,
    Observable,
    Subscription,
    catchError,
    combineLatest,
    filter,
    map,
    of,
    switchMap
} from 'rxjs';

import { MenuItem } from 'primeng/api';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { CurationAgentPanelComponent } from '../CurationAgentPanel/curation-agent-panel.component';
import {
    AgentAdhocSourceResponse,
    CurationAgentSourceStatusEnum
} from '../../../api/agents/models/curation-agent.api';
import { defaultSourceTypeIcon, sourceTypeIcons, sourceTypeLabels } from '../../enums/source-icons.const';
import {
    AgentRouteSectionEnum,
    agentSectionRoute,
    agentSectionSegments,
    automationAgentsRoutePath
} from '../../AutomationAgentsRouter/automation-agents-route.const';
import { CurationAgentModel } from '../../models/curation-agent.model';
import { CurationAgentSourceModel } from '../../models/curation-agent-source.model';
import { CurationAgentsService, sourceNameKey } from '../../services/curation-agents.service';

/**
 * Left hand panel of a single agent: which agent is open, the sections of its dashboard, and the
 * sources feeding it. Follows Social's ThemeSidebarComponent.
 */
@Component({
    selector: 'agpr-agent-sidebar',
    templateUrl: './agent-sidebar.component.html',
    styleUrls: ['./agent-sidebar.component.scss'],
    standalone: false
})
export class AgentSidebarComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly sections = AgentRouteSectionEnum;
    readonly managementRoute: string = `/${automationAgentsRoutePath}`;
    /** Dates are shown in the viewer's own timezone */
    readonly userTimeZone$: Observable<string> = this.curationAgentsService.userTimeZone$;

    agent: CurationAgentModel;
    agents$: Observable<CurationAgentModel[]>;

    /** Which agent the header dropdown is showing, if any */
    showAgentDropdown: boolean = false;

    /** Source rows the user has ticked. Filters the sections to the right of here */
    selectedSourceIds: Record<number, boolean> = {};

    /**
     * Mentions saved from sources the agent no longer has - deleted, or legacy ones the account can't
     * see - which have no row of their own. Ticked to begin with, as the Agents filter's Other is.
     */
    otherSelected: boolean = true;

    /** Past ad hoc runs, listed under the sources when the agent has any */
    adHocEvents: AgentAdhocSourceResponse[] = [];
    selectedAdHocIds: Record<number, boolean> = {};
    showAdHocHistory: boolean = false;

    /** Row menus, held per source so Pause/Activate can follow the source's own state */
    sourceMenus: Map<number, MenuItem[]> = new Map<number, MenuItem[]>();

    private readonly openAgentId$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    private sourceNames: Map<string, string> = new Map<string, string>();
    private readonly subscriptions: Subscription[] = [];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private curationAgentsService: CurationAgentsService,
        private coreDialogService: CoreDialogService,
        private elementRef: ElementRef,
        private localizationService: LocalizationService
    ) {}

    ngOnInit(): void {
        // The open agent heads the list, everything else follows by name
        this.agents$ = combineLatest([
            this.curationAgentsService.agents$,
            this.openAgentId$
        ]).pipe(
            map(([agents, openId]) => [...agents].sort((a, b) => {
                if (a.id === openId || b.id === openId) {
                    return a.id === openId ? -1 : 1;
                }

                return a.name.localeCompare(b.name);
            }))
        );

        this.subscriptions.push(
            this.curationAgentsService.sourceNames$.subscribe(names => this.sourceNames = names),

            // Pause/Activate reads off the status, which the dashboard can change just as easily
            this.curationAgentsService.sourceStatusChanged$.subscribe(() => this.buildSourceMenus()),

            // The sidebar is not routed itself, so it is handed the agent route it sits inside - the
            // one carrying :agent_id. Its own children are the sections.
            this.route.paramMap.pipe(
                map(params => Number(params.get('agent_id'))),
                filter(id => !!id)
            ).subscribe(id => this.curationAgentsService.setCurrentAgent(id)),

            // Read rather than loaded: the dashboard lists the same sources, and both being bound
            // to one instance is what keeps a pause or a removal in step between them
            this.curationAgentsService.currentAgent$.subscribe(agent => {
                this.agent = agent;
                this.openAgentId$.next(agent.id);
                this.selectAllSources();
                this.buildSourceMenus();
                this.loadAdHocRuns(agent.id);
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    /** Complete rows only - a half filled source has nothing to show against it */
    get sources(): CurationAgentSourceModel[] {
        return this.agent?.sources?.filter(source => source.isComplete()) ?? [];
    }

    sectionRoute(section: AgentRouteSectionEnum): string {
        return this.agent ? agentSectionRoute(this.agent.id, section) : this.managementRoute;
    }

    /** Where the header dropdown sends you: the same section, on a different agent */
    agentRoute(agent: CurationAgentModel): string {
        return agentSectionRoute(agent.id, this.currentSection);
    }

    toggleAgentDropdown(): void {
        this.showAgentDropdown = !this.showAgentDropdown;
    }

    closeAgentDropdown(): void {
        this.showAgentDropdown = false;
    }

    sourceIcon(source: CurationAgentSourceModel): string {
        return sourceTypeIcons[source.dataSource?.type] ?? defaultSourceTypeIcon;
    }

    /** Resolved from the shared lookup, falling back to the raw reference until it loads */
    sourceName(source: CurationAgentSourceModel): string {
        const id = source.dataSource?.id;

        return this.sourceNames.get(sourceNameKey(source.dataSource?.type, id))
            ?? source.dataSource?.dataSourceName
            ?? String(id ?? '');
    }

    toggleAdHocHistory(): void {
        this.showAdHocHistory = !this.showAdHocHistory;
    }

    closeAdHocHistory(): void {
        this.showAdHocHistory = false;
    }

    /**
     * Ticking the group tracks every run at once. Clearing it clears them all - which is also what a
     * partly selected group does, so the tick is a way back to none rather than a no-op.
     */
    adHocGroupChanged(selected: boolean): void {
        this.adHocEvents.forEach(event => this.selectedAdHocIds[event.id] = selected);
        this.publishSelection();
    }

    get selectedAdHocCount(): number {
        return this.adHocEvents.filter(event => this.selectedAdHocIds[event.id]).length;
    }

    get allAdHocSelected(): boolean {
        return this.adHocEvents.length > 0 && this.selectedAdHocCount === this.adHocEvents.length;
    }

    /** Some but not all: the checkbox shows its partial state rather than ticked or clear */
    get someAdHocSelected(): boolean {
        const selected = this.selectedAdHocCount;

        return selected > 0 && selected < this.adHocEvents.length;
    }

    adHocSelectionChanged(): void {
        this.publishSelection();
    }

    get selectedSourceCount(): number {
        return this.sources.filter(source => this.selectedSourceIds[source.id]).length;
    }

    /** Everything ticked across sources, Other and ad hoc runs - which must never reach zero */
    private get selectedCount(): number {
        return this.selectedSourceCount + this.selectedAdHocCount + (this.otherSelected ? 1 : 0);
    }

    /**
     * Something has to stay ticked - with nothing ticked there is nothing to report on - so the last
     * ticked source or ad hoc run cannot be cleared.
     */
    isLastSelectedSource(source: CurationAgentSourceModel): boolean {
        return !!this.selectedSourceIds[source.id] && this.selectedCount === 1;
    }

    isLastSelectedAdHocEvent(event: AgentAdhocSourceResponse): boolean {
        return !!this.selectedAdHocIds[event.id] && this.selectedCount === 1;
    }

    get isLastSelectedOther(): boolean {
        return this.otherSelected && this.selectedCount === 1;
    }

    /** Clearing the group clears every run, which with nothing else ticked would leave nothing */
    get adHocGroupLocked(): boolean {
        return this.allAdHocSelected && this.selectedCount === this.selectedAdHocCount;
    }

    otherSelectionChanged(): void {
        this.publishSelection();
    }

    /** Narrows the report to this source alone, as Social Listening's isolate does */
    isolate(source: CurationAgentSourceModel): void {
        this.selectedSourceIds = {};
        this.selectedSourceIds[source.id] = true;
        this.selectedAdHocIds = {};
        this.otherSelected = false;
        this.publishSelection();
    }

    /** Ticking a source narrows what the section to the right is reporting on */
    sourceSelectionChanged(): void {
        this.publishSelection();
    }

    /** Pause an active or standby source, activate a paused one */
    togglePaused(source: CurationAgentSourceModel): void {
        const previousStatus = source.status;

        source.status = this.canPause(source)
            ? CurationAgentSourceStatusEnum.paused
            : CurationAgentSourceStatusEnum.active;

        this.buildSourceMenus();
        // The menus are rebuilt again when the call lands - a failure puts the source back - which
        // the sourceStatusChanged subscription above takes care of
        this.curationAgentsService.setSourceStatus(this.agent, source, previousStatus).subscribe();
    }

    removeSource(source: CurationAgentSourceModel): void {
        this.curationAgentsService.removeSource(this.agent, source).subscribe(() => {
            this.agent.sources = this.agent.sources.filter(row => row !== source);
            delete this.selectedSourceIds[source.id];
            this.buildSourceMenus();

            // Removing the last ticked row would leave nothing in scope, so start over from everything
            if (this.selectedCount === 0) {
                this.selectAllSources();
                this.selectAllAdHocEvents();
            } else {
                this.publishSelection();
            }
        });
    }

    /** Spec: the type icon carries a tooltip naming the source type */
    sourceTypeLabel(source: CurationAgentSourceModel): string {
        return sourceTypeLabels[source.dataSource?.type] ?? 'AutomationAgents.notAvailable';
    }

    sourceStatusLabel(source: CurationAgentSourceModel): string {
        switch (source.status) {
            case CurationAgentSourceStatusEnum.active:
                return 'AutomationAgents.card.source.active';
            case CurationAgentSourceStatusEnum.error:
                return 'AutomationAgents.agent.source.error';
            case CurationAgentSourceStatusEnum.standby:
                return 'AutomationAgents.card.source.standby';
            default:
                return 'AutomationAgents.card.source.paused';
        }
    }

    /**
     * Sources are edited as part of the agent, so adding one opens the agent's panel with its
     * Sources list, the same panel the pencil opens.
     */
    addSource(): void {
        this.openAgentPanel();
    }

    /** Opens the agent in the same panel the management list uses. Kept off the dropdown toggle. */
    editAgent(event: MouseEvent): void {
        event.stopPropagation();
        this.closeAgentDropdown();
        this.openAgentPanel();
    }

    private openAgentPanel(): void {
        this.coreDialogService.open(CurationAgentPanelComponent, {
            inputs: {
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                overlayContainer: document.getElementById('main-content') ?? this.elementRef.nativeElement,
                agent: this.agent
            }
        });
    }

    /** The open section, read back from the child route's path segment */
    private get currentSection(): AgentRouteSectionEnum {
        const segment = this.route.snapshot.firstChild?.routeConfig?.path;
        const section = Object.values(AgentRouteSectionEnum)
            .find(value => agentSectionSegments[value] === segment);

        return section ?? AgentRouteSectionEnum.dashboard;
    }

    /**
     * One menu per source: Isolate, Pause or Activate depending on where the source stands, and
     * Remove. Rebuilt whenever a status changes so the middle item stays right.
     */
    private buildSourceMenus(): void {
        this.sourceMenus = new Map<number, MenuItem[]>();

        this.sources.forEach(source => this.sourceMenus.set(source.id, [
            {
                label: this.localizationService.transform('AutomationAgents.agent.source.isolate'),
                command: () => this.isolate(source)
            },
            {
                label: this.localizationService.transform(
                    this.canPause(source)
                        ? 'AutomationAgents.agent.source.pause'
                        : 'AutomationAgents.agent.source.activate'
                ),
                command: () => this.togglePaused(source)
            },
            {
                label: this.localizationService.transform('AutomationAgents.agent.source.remove'),
                command: () => this.removeSource(source)
            }
        ]));
    }

    /** Standby is still switched on - only parked for quota - so it offers Pause rather than Activate */
    private canPause(source: CurationAgentSourceModel): boolean {
        return source.isActive || source.isStandby;
    }

    /** Everything is in scope until the user narrows it */
    private selectAllSources(): void {
        this.selectedSourceIds = {};
        this.sources.forEach(source => this.selectedSourceIds[source.id] = true);
        this.otherSelected = true;
        this.publishSelection();
    }

    private loadAdHocRuns(agentId: number): void {
        this.subscriptions.push(
            this.curationAgentsService.adHocRuns(agentId).subscribe(runs => {
                this.adHocEvents = runs ?? [];
                this.selectAllAdHocEvents();
            })
        );
    }

    /** Everything is in scope until the user narrows it */
    private selectAllAdHocEvents(): void {
        this.selectedAdHocIds = {};
        this.adHocEvents.forEach(event => this.selectedAdHocIds[event.id] = true);
        this.publishSelection();
    }

    /**
     * With everything ticked the ticked lists go out as null, and so does the scope, so the sections
     * send the agent filter alone rather than listing every source and run. Otherwise the scope is
     * exclusions of what was unticked while Other is ticked, and inclusions of what is still ticked
     * once it isn't - see AgentScopeModel.
     */
    private publishSelection(): void {
        const sourceIds = this.sources.filter(source => this.selectedSourceIds[source.id]).map(source => source.id);
        const adHocIds = this.adHocEvents.filter(event => this.selectedAdHocIds[event.id]).map(event => event.id);
        const allListedSelected = sourceIds.length === this.sources.length && adHocIds.length === this.adHocEvents.length;
        const allSelected = allListedSelected && this.otherSelected;

        this.curationAgentsService.setSelectedSources(allListedSelected ? null : sourceIds);
        this.curationAgentsService.setSelectedAdHocEvents(allListedSelected ? null : adHocIds);

        if (allSelected) {
            this.curationAgentsService.setAgentScope(null);
        } else if (this.otherSelected) {
            this.curationAgentsService.setAgentScope({
                sources: {
                    include: [],
                    exclude: this.sources.filter(source => !this.selectedSourceIds[source.id]).map(source => source.id)
                },
                adHocRuns: {
                    include: [],
                    exclude: this.adHocEvents.filter(event => !this.selectedAdHocIds[event.id]).map(event => event.id)
                }
            });
        } else {
            this.curationAgentsService.setAgentScope({
                sources: { include: sourceIds, exclude: [] },
                adHocRuns: { include: adHocIds, exclude: [] }
            });
        }
    }
}
